<template>
  <div class="w-full">
    <!-- Trigger Row -->
    <div class="flex items-baseline min-h-[20px] leading-[20px]">
      <!-- Toggle Slot -->
      <div class="shrink-0 w-[20px] min-w-[20px] inline-flex items-baseline justify-start leading-none">
        <button
          v-if="showToggle"
          :id="triggerId"
          type="button"
          :aria-expanded="expanded"
          :aria-controls="contentId"
          :aria-label="ariaLabel || (expanded ? 'Collapse' : 'Expand')"
          class="inline-flex items-baseline justify-center p-0 m-0 border-0 bg-transparent cursor-pointer rounded-sm leading-none hover:bg-neutral-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-purple-800 focus-visible:outline-offset-2"
          @click="toggle"
          @keydown.enter.prevent="toggle"
          @keydown.space.prevent="toggle"
        >
          <VIcon
            :name="expanded ? 'chevron-down-line' : 'chevron-right-line'"
            class="inline-block w-3 h-3 align-text-bottom shrink-0"
            aria-hidden="true"
          />
        </button>
        <span v-else class="inline-block w-3 h-3 align-text-bottom" aria-hidden="true" />
      </div>

      <!-- Trigger Content -->
      <div class="flex-1 min-w-0">
        <slot name="trigger" />
      </div>
    </div>

    <!-- Collapsible Content -->
    <section
      :id="contentId"
      :aria-labelledby="triggerId"
      :class="['ml-[20px] overflow-hidden', expanded ? 'block' : 'hidden']"
    >
      <slot />
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { VIcon } from '@vonage/vivid-vue';

const props = withDefaults(
  defineProps<{
    defaultExpanded?: boolean;
    contentId?: string;
    triggerId?: string;
    ariaLabel?: string;
    showToggle?: boolean;
  }>(),
  {
    defaultExpanded: true,
    contentId: undefined,
    triggerId: undefined,
    ariaLabel: undefined,
    showToggle: true,
  }
);

const expanded = ref(props.defaultExpanded);

const contentId = computed(() => props.contentId ?? `collapsible-content-${crypto.randomUUID()}`);

const triggerId = computed(() => props.triggerId ?? `collapsible-trigger-${crypto.randomUUID()}`);

function toggle() {
  expanded.value = !expanded.value;
}
</script>

<style scoped>
/* Ensure SVG inside VIcon scales properly */
:deep(svg) {
  width: 12px;
  height: 12px;
  display: block;
}
</style>
